import { SearchX, Users } from 'lucide-react';
import { useDeferredValue, useMemo, useState } from 'react';
import { isNetworkFailure } from '../api/client';
import { useMembers } from '../api/members';
import { useMe } from '../auth/AuthProvider';
import { Page } from '../components/layout';
import { Card, CenteredSpinner, EmptyState, ErrorBox } from '../components/ui';
import { useT } from '../i18n';
import { searchTokens } from '../lib/search';
import { groupMembers, isListed, type MemberGroup } from './choir/groups';
import { groupColor, groupLabelKey, MemberRow } from './choir/MemberRow';
import { SearchField } from './choir/SearchField';

const SEARCH_KEY = 'kgr.choir.search';

function readSearch(): string {
  try {
    return sessionStorage.getItem(SEARCH_KEY) ?? '';
  } catch {
    return '';
  }
}

function GroupCard({ group, meId }: { group: MemberGroup; meId: string | undefined }) {
  const t = useT();
  const color = groupColor(group.key);
  const count = group.members.length;
  return (
    <Card as="section" className="overflow-hidden">
      <header className="flex items-center gap-3 border-b border-[var(--line)] px-4 py-3" style={{ borderTop: `4px solid ${color}` }}>
        <span className="size-3 shrink-0 rounded-full" style={{ background: color }} aria-hidden />
        <h2 className="flex-1 text-lg font-bold">{t(groupLabelKey(group.key))}</h2>
        <span
          className="min-w-8 rounded-full px-2.5 py-0.5 text-center text-[15px] font-bold text-white"
          style={{ background: color }}
          aria-label={t('choir.group_count', { count })}
        >
          {count}
        </span>
      </header>
      <ul className="divide-y divide-[var(--line)]">
        {group.members.map((m) => (
          <MemberRow key={m.id} m={m} to={`/choir/${m.id}`} isMe={m.id === meId} />
        ))}
      </ul>
    </Card>
  );
}

export default function ChoirScreen() {
  const t = useT();
  const me = useMe();
  const q = useMembers();
  const [search, setSearchState] = useState(readSearch);
  const deferred = useDeferredValue(search);

  const setSearch = (v: string) => {
    setSearchState(v);
    try {
      sessionStorage.setItem(SEARCH_KEY, v);
    } catch {
      /* ignore */
    }
  };

  const tokens = useMemo(() => searchTokens(deferred), [deferred]);
  const groups = useMemo(() => (q.data ? groupMembers(q.data, tokens) : []), [q.data, tokens]);
  const total = useMemo(() => q.data?.filter(isListed).length ?? 0, [q.data]);

  let body;
  if (q.isPending && q.fetchStatus === 'paused') {
    // offlineFirst: with no connection and nothing cached the query pauses instead of failing.
    body = (
      <EmptyState icon={Users} title={t('common.offline')}>
        {t('common.no_data_offline')}
      </EmptyState>
    );
  } else if (q.isPending) {
    body = <CenteredSpinner />;
  } else if (!q.data) {
    body = isNetworkFailure(q.error) ? (
      <EmptyState icon={Users} title={t('common.offline')}>
        {t('common.no_data_offline')}
      </EmptyState>
    ) : (
      <ErrorBox onRetry={() => void q.refetch()} retryLabel={t('common.retry')}>
        {t('common.error')}
      </ErrorBox>
    );
  } else if (total === 0) {
    body = <EmptyState icon={Users} title={t('choir.empty')} />;
  } else if (groups.length === 0) {
    body = (
      <EmptyState icon={SearchX} title={t('choir.no_results')}>
        {t('choir.no_results_hint')}
      </EmptyState>
    );
  } else {
    body = (
      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 md:gap-5">
        {groups.map((g) => (
          <GroupCard key={g.key} group={g} meId={me?.id} />
        ))}
      </div>
    );
  }

  return (
    <Page
      title={t('choir.title')}
      wide
      actions={q.data && total > 0 ? <span className="text-[15px] font-medium text-[var(--muted)]">{t('choir.total', { count: total })}</span> : null}
    >
      <div className="mb-5">
        <SearchField value={search} onChange={setSearch} placeholder={t('choir.search')} />
      </div>
      {body}
    </Page>
  );
}
